import { Component } from '@theme/component';

/**
 * @typedef {Object} CollectionDescriptionRefs
 * @property {HTMLElement} text
 * @property {HTMLElement} moreButton
 * @property {HTMLElement} lessButton
 */

/**
 * Truncates a collection description to a fixed number of lines and toggles
 * between the collapsed and expanded states.
 *
 * @extends {Component<CollectionDescriptionRefs>}
 */
class CollectionDescriptionComponent extends Component {
  requiredRefs = ['text', 'moreButton', 'lessButton'];

  /**
   * The original description markup, kept so it can be re-truncated on resize.
   *
   * @type {string}
   */
  #content = '';

  /**
   * @type {number}
   */
  #totalChars = 0;

  /**
   * @type {number}
   */
  #lineCount = 4;

  /**
   * @type {number}
   */
  #lastWidth = 0;

  connectedCallback() {
    super.connectedCallback();

    const lineCount = parseFloat(getComputedStyle(this).getPropertyValue('--collection-description-lines'));

    if (!Number.isNaN(lineCount)) this.#lineCount = lineCount;

    this.#capture();
    this.#updateLayout();
    this.#resizeObserver.observe(this);
    document.fonts.ready.then(this.#updateLayout);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Re-runs the layout when the section rendering API re-renders this element.
   */
  updatedCallback() {
    this.#capture();
    this.#updateLayout();
  }

  /**
   * Toggles between the collapsed and expanded states.
   */
  toggle = () => {
    const expanded = !(this.dataset.expanded === 'true');

    this.dataset.expanded = String(expanded);

    for (const button of [this.refs.moreButton, this.refs.lessButton]) {
      button.setAttribute('aria-expanded', String(expanded));
    }

    this.#updateLayout();
  };

  /**
   * Stores the rendered description so it can be truncated without losing
   * the original markup.
   */
  #capture() {
    const { text } = this.refs;

    if (text.querySelector('.collection-description__toggle')) return;

    this.#content = text.innerHTML;
    this.#totalChars = this.#countChars(this.#fragment());
  }

  /**
   * @param {Node} root
   * @returns {number}
   */
  #countChars(root) {
    let count = 0;

    for (const textNode of this.#textNodes(root)) count += textNode.data.length;

    return count;
  }

  /**
   * @returns {DocumentFragment}
   */
  #fragment() {
    const template = document.createElement('template');

    template.innerHTML = this.#content;

    return template.content;
  }

  /**
   * Collects the text nodes of a subtree in document order.
   *
   * @param {Node} root
   * @returns {Text[]}
   */
  #textNodes(root) {
    /** @type {Text[]} */
    const nodes = [];

    for (const child of root.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) nodes.push(/** @type {Text} */ (child));
      else nodes.push(...this.#textNodes(child));
    }

    return nodes;
  }

  #updateLayout = () => {
    const { text, moreButton, lessButton } = this.refs;
    const lineHeight = parseFloat(getComputedStyle(text).lineHeight);
    const maxHeight = lineHeight * this.#lineCount + 1;

    if (this.dataset.expanded === 'true') {
      this.#render(this.#totalChars, lessButton);
      return;
    }

    this.#render(this.#totalChars, null);

    if (text.scrollHeight <= maxHeight) return;

    let low = 0;
    let high = this.#totalChars;

    while (low < high) {
      const mid = Math.ceil((low + high) / 2);

      if (this.#fits(mid, maxHeight)) low = mid;
      else high = mid - 1;
    }

    this.#render(low, moreButton);
  };

  /**
   * @param {number} limit
   * @param {number} maxHeight
   * @returns {boolean}
   */
  #fits(limit, maxHeight) {
    this.#render(limit, this.refs.moreButton);

    return this.refs.text.scrollHeight <= maxHeight;
  }

  /**
   * Renders the description truncated to `limit` characters, with the toggle
   * placed inline so the text wraps around it.
   *
   * @param {number} limit
   * @param {HTMLElement | null} button
   */
  #render(limit, button) {
    const fragment = this.#fragment();
    const truncated = limit < this.#totalChars;

    if (truncated) this.#truncate(fragment, limit);

    const parent = this.#lastTextParent(fragment) ?? this.refs.text;

    if (truncated) parent.append('…');
    if (button) parent.append(' ', button);

    this.refs.text.replaceChildren(fragment);
  }

  /**
   * Empties every text node past `limit` characters and drops the trailing
   * partial word so the copy never breaks mid-word.
   *
   * @param {DocumentFragment} fragment
   * @param {number} limit
   */
  #truncate(fragment, limit) {
    const nodes = this.#textNodes(fragment);
    let remaining = limit;

    for (const textNode of nodes) {
      if (remaining <= 0) textNode.data = '';
      else if (textNode.data.length <= remaining) remaining -= textNode.data.length;
      else {
        textNode.data = textNode.data.slice(0, remaining);
        remaining = 0;
      }
    }

    const last = nodes.filter((textNode) => textNode.data.trim()).pop();

    if (last) last.data = last.data.replace(/\s*\S*$/, '');
  }

  /**
   * @param {DocumentFragment} fragment
   * @returns {HTMLElement | null}
   */
  #lastTextParent(fragment) {
    let parent = null;

    for (const textNode of this.#textNodes(fragment)) {
      if (textNode.data.trim()) parent = textNode.parentElement;
    }

    return parent;
  }

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeObserver((entries) => {
    const width = entries[0]?.contentRect.width ?? 0;

    if (width === this.#lastWidth) return;

    this.#lastWidth = width;
    this.#updateLayout();
  });
}

if (!customElements.get('collection-description-component')) {
  customElements.define('collection-description-component', CollectionDescriptionComponent);
}
